import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { addCalendarDays, type SmartAction } from '@mlm/shared/coaching';
import { CoachButton, SageCheck } from '../primitives';
import { coaching } from '../../lib/api';
import { useRefreshCoaching } from '../../lib/queries';

const APPLIED_KEY = 'kala-applied-actions';

function readApplied(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(APPLIED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function rememberApplied(ids: Set<string>) {
  try {
    localStorage.setItem(APPLIED_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    /* storage unavailable: the button simply shows again after a reload */
  }
}

const buttonLabel = (a: SmartAction) =>
  a.label ??
  (a.kind === 'add_action'
    ? a.when === 'tomorrow'
      ? 'Add to tomorrow'
      : "Add to today's actions"
    : a.kind === 'save_affirmation'
      ? 'Save affirmation'
      : a.progressPercent !== undefined
        ? `Set progress to ${a.progressPercent}%`
        : 'Add milestone');

const doneLabel = (a: SmartAction) =>
  a.kind === 'add_action'
    ? a.when === 'tomorrow'
      ? 'Added to tomorrow'
      : 'Added to today'
    : a.kind === 'save_affirmation'
      ? 'Saved to your affirmations'
      : 'Goal updated';

const detail = (a: SmartAction) =>
  a.kind === 'add_action' ? a.title : a.kind === 'save_affirmation' ? a.text : a.milestoneTitle;

/**
 * SC-17: steps Master Kala proposed, as one-tap buttons. A tap calls the
 * ordinary coaching route, so every change keeps its validation and scoring;
 * the model never writes to the member's plan.
 */
export function SmartActions({ actions, today }: { actions: SmartAction[]; today: string | null }) {
  const refresh = useRefreshCoaching();
  const [applied, setApplied] = useState(readApplied);
  const apply = useMutation({
    mutationFn: async (a: SmartAction) => {
      if (a.kind === 'add_action') {
        if (!today) throw new Error('today_unknown');
        await coaching.createAction({
          title: a.title,
          estimatedMinutes: a.estimatedMinutes ?? null,
          priority: a.priority,
          goalId: a.goalId ?? null,
          scheduledDate: a.when === 'tomorrow' ? addCalendarDays(today, 1) : today,
        });
      } else if (a.kind === 'save_affirmation') {
        await coaching.createAffirmation({ text: a.text });
      } else {
        if (a.progressPercent !== undefined) {
          await coaching.updateGoal(a.goalId, { progressPercent: a.progressPercent });
        }
        if (a.milestoneTitle) await coaching.addMilestone(a.goalId, { title: a.milestoneTitle });
      }
      return a.id;
    },
    onSuccess: (id) => {
      const next = new Set(applied).add(id);
      setApplied(next);
      rememberApplied(next);
      coaching.event('coach_action_saved');
      void refresh();
    },
  });

  if (actions.length === 0) return null;
  return (
    <ul className="mt-4 space-y-2">
      {actions.map((a) => (
        <li
          key={a.id}
          className="flex flex-wrap items-center justify-between gap-3 border border-border px-3.5 py-3"
        >
          {detail(a) && <span className="min-w-0 flex-1 text-sm text-ink/80">{detail(a)}</span>}
          {applied.has(a.id) ? (
            <span className="flex items-center gap-2 text-sm text-sage">
              <SageCheck size={16} /> {doneLabel(a)}
            </span>
          ) : (
            <CoachButton disabled={apply.isPending} onClick={() => apply.mutate(a)}>
              {buttonLabel(a)}
            </CoachButton>
          )}
        </li>
      ))}
    </ul>
  );
}
